<template>
  <header class="relative w-full" role="banner">
    <!-- Sidebar Toggle Button (Mobile) -->
    <button
      @click="toggleSidebar"
      class="fixed top-4 left-4 z-50 bg-gradient-to-r from-purple-700 to-blue-500 text-white p-3 rounded-lg flex items-center shadow-lg lg:hidden"
      aria-label="Toggle navigation menu"
      aria-expanded="showSidebar"
      aria-controls="main-navigation"
    >
      <Icon icon="mdi:menu" class="text-xl" aria-hidden="true" />
    </button>

    <!-- Desktop Horizontal Menu (with optimized elements) -->
    <div class="hidden lg:block w-full bg-gray-800 rounded-t-lg overflow-hidden">
      <div class="px-5 py-2.5 flex items-center justify-between border-b border-gray-700/50">
        <!-- Left side: Logo/Brand -->
        <Link href="/" class="flex items-center">
          <ApplicationLogo width="130px" />
        </Link>
        
        <!-- Center: Main Navigation Items -->
        <div class="flex items-center space-x-1.5">
          <!-- Home -->
          <Link
            href="/"
            class="px-3 py-1.5 text-gray-300 hover:text-white transition-colors duration-200 rounded flex items-center space-x-1.5"
            :class="{ 'bg-gray-700/70 text-white': $page.url === '/' }"
            aria-label="Home"
          >
            <Icon icon="mdi:home" class="text-lg text-blue-400" aria-hidden="true" />
            <span class="font-medium text-sm">Home</span>
          </Link>
          
          <!-- Tools -->
          <Link
            href="/tools"
            class="px-3 py-1.5 text-gray-300 hover:text-white transition-colors duration-200 rounded flex items-center space-x-1.5"
            :class="{ 'bg-gray-700/70 text-white': $page.url.startsWith('/tools') }"
            aria-label="Tools"
          >
            <Icon icon="mdi:wrench" class="text-lg text-teal-400" aria-hidden="true" />
            <span class="font-medium text-sm">Tools</span>
          </Link>
          
          <!-- Blog -->
          <Link 
            href="/blog" 
            class="px-3 py-1.5 text-gray-300 hover:text-white transition-colors duration-200 rounded flex items-center space-x-1.5"
            :class="{ 'bg-gray-700/70 text-white': $page.url.startsWith('/blog') }"
            aria-label="Blog"
          >
            <Icon icon="mdi:newspaper" class="text-lg text-cyan-400" aria-hidden="true" />
            <span class="font-medium text-sm">Blog</span>
          </Link>
          
          <!-- Contact -->
          <Link
            href="/contact"
            class="px-3 py-1.5 text-gray-300 hover:text-white transition-colors duration-200 rounded flex items-center space-x-1.5"
            :class="{ 'bg-gray-700/70 text-white': $page.url.startsWith('/contact') }"
            aria-label="Contact"
          >
            <Icon icon="mdi:email" class="text-lg text-purple-400" aria-hidden="true" />
            <span class="font-medium text-sm">Contact</span>
          </Link>
          
          <!-- Submit AI -->
          <Link 
            href="/submit-ai" 
            class="px-3 py-1.5 text-gray-300 hover:text-white transition-colors duration-200 rounded flex items-center space-x-1.5"
            :class="{ 'bg-gray-700/70 text-white': $page.url.startsWith('/submit-ai') }"
            aria-label="Submit AI Tool"
          >
            <Icon icon="mdi:plus-circle" class="text-lg text-green-400" aria-hidden="true" />
            <span class="font-medium text-sm">Submit AI</span>
          </Link>
        </div>
        
        <!-- Right side: User menu -->
        <div class="flex items-center space-x-1.5">
          <!-- User Dashboard - Visible when logged in -->
          <Link
            v-if="auth.user"
            :href="route('dashboard')"
            class="px-3 py-1.5 text-gray-300 hover:text-white transition-colors duration-200 rounded flex items-center space-x-1.5"
            :class="{ 'bg-gray-700/70 text-white': $page.url.startsWith('/dashboard') }"
            aria-label="Dashboard"
          >
            <Icon icon="mdi:view-dashboard-outline" class="text-lg text-green-400" aria-hidden="true" />
            <span class="font-medium text-sm">Dashboard</span>
          </Link>
          
          <!-- Logout - Visible when logged in -->
          <Link
            v-if="auth.user"
            :href="route('logout')"
            method="post"
            as="button"
            class="px-3 py-1.5 text-gray-300 hover:text-white transition-colors duration-200 rounded flex items-center space-x-1.5"
            aria-label="Log Out"
          >
            <Icon icon="mdi:logout" class="text-lg text-pink-400" aria-hidden="true" />
            <span class="font-medium text-sm">Log Out</span>
          </Link>
          
          <!-- Login - Visible when logged out -->
          <Link
            v-if="!auth.user"
            :href="route('login')"
            class="px-3 py-1.5 text-gray-300 hover:text-white transition-colors duration-200 rounded flex items-center space-x-1.5"
            aria-label="Log In"
          >
            <Icon icon="mdi:login" class="text-lg text-blue-400" aria-hidden="true" />
            <span class="font-medium text-sm">Log In</span>
          </Link>
          
          <!-- Register - Visible when logged out -->
          <Link
            v-if="!auth.user"
            :href="route('register')"
            class="px-3 py-1.5 text-gray-300 hover:text-white transition-colors duration-200 rounded flex items-center space-x-1.5"
            aria-label="Register"
          >
            <Icon icon="mdi:account-plus" class="text-lg text-yellow-400" aria-hidden="true" />
            <span class="font-medium text-sm">Register</span>
          </Link>
        </div>
      </div>
    </div>
    
    <!-- Spacer for fixed header on large screens -->
    <div class="hidden lg:block h-12"></div>

    <!-- Sidebar Navigation (Mobile) -->
    <div
      :class="[
        'fixed top-0 left-0 h-full bg-gray-900 p-6 z-50 transform transition-transform duration-300 ease-in-out shadow-lg',
        { 'translate-x-0': showSidebar, '-translate-x-full': !showSidebar },
      ]"
      style="width: 280px; border-right: 2px solid rgba(255, 255, 255, 0.1)"
      id="main-navigation"
      role="navigation"
      aria-label="Main Navigation"
    >
      <!-- Logo and Close Button -->
      <div class="flex items-center justify-between mb-8">
        <Link :href="route('landing')" aria-label="Balan.ai Home">
          <div
            class="transform transition-all duration-300 hover:translate-y-1"
            style="margin-left: -10px"
          >
            <ApplicationLogo class="h-12 fill-current text-white" />
          </div>
        </Link>
        <button
          @click="toggleSidebar"
          class="text-gray-400 focus:outline-none hover:text-blue-400 transition duration-200 transform hover:scale-105"
          aria-label="Close navigation menu"
        >
          <Icon icon="mdi:close" class="text-[22px]" aria-hidden="true" />
        </button>
      </div>

      <!-- Main Navigation Links -->
      <nav class="space-y-6 mb-8">
        <Link
          href="/"
          class="flex items-center space-x-3 text-gray-300 hover:text-teal-400 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-teal-400"
          :class="{ 'text-teal-400': route().current('landing') }"
          @click="toggleSidebar"
        >
          <Icon icon="mdi:home" class="text-teal-400 text-xl transition duration-150" aria-hidden="true" />
          <span class="text-lg font-medium tracking-wide">Home</span>
        </Link>
        <Link
          href="/tools"
          class="flex items-center space-x-3 text-gray-300 hover:text-teal-400 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-teal-400"
          :class="{ 'text-teal-400': route().current('tools') }"
          @click="toggleSidebar"
        >
          <Icon icon="mdi:wrench" class="text-teal-400 text-xl transition duration-150" aria-hidden="true" />
          <span class="text-lg font-medium tracking-wide">Tools</span>
        </Link>
        <Link
          href="/blog"
          class="flex items-center space-x-3 text-gray-300 hover:text-cyan-400 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-cyan-400"
          :class="{ 'text-cyan-400': route().current('blog') }"
          @click="toggleSidebar"
        >
          <Icon icon="mdi:newspaper" class="text-cyan-400 text-xl transition duration-150" aria-hidden="true" />
          <span class="text-lg font-medium tracking-wide">Blog</span>
        </Link>
        <Link
          href="/contact"
          class="flex items-center space-x-3 text-gray-300 hover:text-purple-400 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-purple-400"
          :class="{ 'text-purple-400': route().current('contact') }"
          @click="toggleSidebar"
        >
          <Icon icon="mdi:email-outline" class="text-purple-400 text-xl transition duration-150" aria-hidden="true" />
          <span class="text-lg font-medium tracking-wide">Contact</span>
        </Link>
        <Link
          href="/submit-ai"
          class="flex items-center space-x-3 text-gray-300 hover:text-green-400 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-green-400"
          :class="{ 'text-green-400': route().current('submit-ai') }"
          @click="toggleSidebar"
        >
          <Icon icon="mdi:plus-circle" class="text-green-400 text-xl transition duration-150" aria-hidden="true" />
          <span class="text-lg font-medium tracking-wide">Submit AI</span>
        </Link>
      </nav>

      <!-- Profile Related Options -->
      <div class="mt-auto border-t border-gray-700 pt-6">
        <nav class="space-y-6">
          <Link
            v-if="auth.user"
            :href="route('dashboard')"
            class="flex items-center space-x-3 text-gray-300 hover:text-green-400 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-green-400"
            :class="{ 'text-green-400': route().current('dashboard') }"
            @click="toggleSidebar"
          >
            <Icon
              icon="mdi:view-dashboard-outline"
              class="text-green-400 text-xl transition duration-150"
              aria-hidden="true"
            />
            <span class="text-lg font-medium tracking-wide">Dashboard</span>
          </Link>
          <Link
            v-if="auth.user"
            :href="route('favorites')"
            class="flex items-center space-x-3 text-gray-300 hover:text-red-400 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-red-400"
            :class="{ 'text-red-400': route().current('favorites') }"
            @click="toggleSidebar"
          >
            <Icon icon="mdi:heart-outline" class="text-red-400 text-xl transition duration-150" aria-hidden="true" />
            <span class="text-lg font-medium tracking-wide">Favorites</span>
          </Link>
          <Link
            v-if="auth.user"
            :href="route('history.index')"
            class="flex items-center space-x-3 text-gray-300 hover:text-yellow-400 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-yellow-400"
            :class="{ 'text-yellow-400': route().current('history.index') }"
            @click="toggleSidebar"
          >
            <Icon icon="mdi:history" class="text-yellow-400 text-xl transition duration-150" aria-hidden="true" />
            <span class="text-lg font-medium tracking-wide">History</span>
          </Link>
          <Link
            v-if="auth.user"
            :href="route('profile.edit')"
            class="flex items-center space-x-3 text-gray-300 hover:text-indigo-400 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-indigo-400"
            :class="{ 'text-indigo-400': route().current('profile.edit') }"
            @click="toggleSidebar"
          >
            <Icon
              icon="mdi:account-outline"
              class="text-indigo-400 text-xl transition duration-150"
              aria-hidden="true"
            />
            <span class="text-lg font-medium tracking-wide">Profile</span>
          </Link>
          <Link
            v-if="auth.user"
            :href="route('my-ai-tools')"
            class="flex items-center space-x-3 text-gray-300 hover:text-indigo-400 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-indigo-400"
            :class="{ 'text-indigo-400': route().current('my-ai-tools') }"
            @click="toggleSidebar"
          >
            <Icon icon="mdi:robot-outline" class="text-indigo-400 text-xl transition duration-150" aria-hidden="true" />
            <span class="text-lg font-medium tracking-wide">My AI Tools</span>
          </Link>
          <Link
            v-if="auth.user"
            :href="route('logout')"
            method="post"
            as="button"
            class="flex items-center space-x-3 text-gray-300 hover:text-pink-400 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-pink-400"
            @click="toggleSidebar"
          >
            <Icon icon="mdi:logout" class="text-pink-400 text-xl transition duration-150" aria-hidden="true" />
            <span class="text-lg font-medium tracking-wide">Log Out</span>
          </Link>
          <template v-else>
            <Link
              :href="route('login')"
              class="flex items-center space-x-3 text-gray-300 hover:text-blue-400 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-400"
              @click="toggleSidebar"
            >
              <Icon icon="mdi:login" class="text-blue-400 text-xl transition duration-150" aria-hidden="true" />
              <span class="text-lg font-medium tracking-wide">Log in</span>
            </Link>
            <Link
              :href="route('register')"
              class="flex items-center space-x-3 text-gray-300 hover:text-yellow-400 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-yellow-400"
              @click="toggleSidebar"
            >
              <Icon
                icon="mdi:account-plus"
                class="text-yellow-400 text-xl transition duration-150"
                aria-hidden="true"
              />
              <span class="text-lg font-medium tracking-wide">Register</span>
            </Link>
          </template>
        </nav>
      </div>
    </div>

    <!-- Overlay (Behind Sidebar) -->
    <div
      :class="[
        'fixed inset-0 bg-black bg-opacity-50 z-40 transition-opacity duration-300 ease-in-out',
        { 'opacity-100': showSidebar, 'opacity-0 pointer-events-none': !showSidebar },
      ]"
      @click="toggleSidebar"
      aria-hidden="true"
    ></div>
  </header>
</template>

<script setup>
  import { ref, onMounted, onUnmounted } from 'vue';
  import { Link } from '@inertiajs/vue3';
  import { Icon } from '@iconify/vue';
  import ApplicationLogo from '@/Components/ApplicationLogo.vue';
  import { usePage } from '@inertiajs/vue3';

  const showSidebar = ref(false);
  const { auth } = usePage().props;

  const toggleSidebar = () => {
    showSidebar.value = !showSidebar.value;
    
    // Announce to screen readers
    if (showSidebar.value) {
      announceToScreenReader("Navigation menu opened");
    } else {
      announceToScreenReader("Navigation menu closed");
    }
  };

  const closeSidebarOnResize = () => {
    if (window.innerWidth > 768 && showSidebar.value) {
      showSidebar.value = false;
    }
  };
  
  // Close sidebar on ESC key
  const handleKeydown = (event) => {
    if (event.key === 'Escape' && showSidebar.value) {
      showSidebar.value = false;
    }
  };
  
  // Helper function for screen reader announcements
  const announceToScreenReader = (message) => {
    const announcement = document.createElement('div');
    announcement.setAttribute('aria-live', 'polite');
    announcement.classList.add('sr-only');
    announcement.textContent = message;
    document.body.appendChild(announcement);
    
    // Clean up after announcement is read
    setTimeout(() => {
      document.body.removeChild(announcement);
    }, 1000);
  };

  onMounted(() => {
    window.addEventListener('resize', closeSidebarOnResize);
    document.addEventListener('keydown', handleKeydown);
  });

  onUnmounted(() => {
    window.removeEventListener('resize', closeSidebarOnResize);
    document.removeEventListener('keydown', handleKeydown);
  });
</script>

<style scoped>
  button {
    border: none;
    background-color: transparent;
  }

  /* Mobile sidebar navigation styles */
  nav a {
    transition: all 0.3s ease;
  }

  nav a:hover {
    transform: translateX(8px);
  }
  
  /* Focus styles for keyboard navigation */
  a:focus-visible, button:focus-visible {
    outline: 2px solid #3b82f6;
    outline-offset: 2px;
  }
  
  /* Screen reader only class */
  .sr-only {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border-width: 0;
  }
  
  /* Reduced motion preferences */
  @media (prefers-reduced-motion: reduce) {
    button, a, .transition-all, .transition-transform, .transition-colors {
      transition: none !important;
    }
    
    nav a:hover {
      transform: none;
    }
  }
</style>